import React, { useState, useEffect, useMemo, useCallback } from 'react';
import type {
  UsageAnalyticsData,
  UsageViewMode,
  UsageSourceColumn,
  UsageLogEvent,
} from '../../types/usage';
import {
  getLocalUsageAnalytics,
  fetchUsageAnalyticsFromServer,
  clearUsageLogsApi,
  exportUsageToCsv,
  onUsageUpdate,
} from '../../utils/usageLogger';
import { matchesSearch } from '../../utils/searchUtils';

interface UsageAnalyticsTabProps {
  isSuperAdmin?: boolean;
}

interface MetricsSummary {
  totalOpens: number;
  uniqueUsers: number;
  uniqueFiles: number;
  topFile: { name: string; count: number } | null;
  topUser: { name: string; count: number } | null;
}

export const UsageAnalyticsTab: React.FC<UsageAnalyticsTabProps> = ({ isSuperAdmin = false }) => {
  const [data, setData] = useState<UsageAnalyticsData>(() => getLocalUsageAnalytics());
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<UsageViewMode>('by_user');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSource, setSelectedSource] = useState<UsageSourceColumn | 'All'>('All');
  const [dateRange, setDateRange] = useState<'all' | 'today' | '7days' | '30days'>('all');
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [expandedFile, setExpandedFile] = useState<string | null>(null);
  const [showClearConfirmModal, setShowClearConfirmModal] = useState(false);
  const [clearInputConfirm, setClearInputConfirm] = useState('');
  const [clearLoading, setClearLoading] = useState(false);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

  // Load latest from server on mount
  const refreshData = useCallback(async () => {
    setLoading(true);
    try {
      const serverData = await fetchUsageAnalyticsFromServer();
      setData(serverData);
    } catch (e) {
      console.warn('Failed refreshing usage analytics:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshData();

    // Subscribe to cross-tab updates
    const unsubscribe = onUsageUpdate((updated) => {
      setData(updated);
    });

    return () => {
      unsubscribe();
    };
  }, [refreshData]);

  // Handle clearing logs
  const handleClearLogs = async () => {
    if (clearInputConfirm.trim().toUpperCase() !== 'CLEAR') return;
    setClearLoading(true);
    try {
      await clearUsageLogsApi();
      const fresh = getLocalUsageAnalytics();
      setData(fresh);
      setShowClearConfirmModal(false);
      setClearInputConfirm('');
    } catch (e) {
      console.error('Error clearing logs:', e);
    } finally {
      setClearLoading(false);
    }
  };

  // Helper date checker
  const isDateInRange = useCallback((isoString: string): boolean => {
    if (dateRange === 'all') return true;
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return true;
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();

    if (dateRange === 'today') {
      return (
        date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear()
      );
    }
    if (dateRange === '7days') {
      return diffMs <= 7 * 24 * 60 * 60 * 1000;
    }
    if (dateRange === '30days') {
      return diffMs <= 30 * 24 * 60 * 60 * 1000;
    }
    return true;
  }, [dateRange]);

  // Top metric computations
  const metrics = useMemo<MetricsSummary>(() => {
    const userKeys = Object.keys(data.userSummaries || {});
    const fileKeys = Object.keys(data.fileSummaries || {});

    let topFile: { name: string; count: number } | null = null;
    Object.values(data.fileSummaries || {}).forEach((f) => {
      if (!topFile || f.totalOpens > topFile.count) {
        topFile = { name: f.fileName, count: f.totalOpens };
      }
    });

    let topUser: { name: string; count: number } | null = null;
    Object.values(data.userSummaries || {}).forEach((u) => {
      if (!topUser || u.totalOpens > topUser.count) {
        topUser = { name: u.userName || u.userEmail, count: u.totalOpens };
      }
    });

    return {
      totalOpens: data.totalOpens || 0,
      uniqueUsers: userKeys.length,
      uniqueFiles: fileKeys.length,
      topFile,
      topUser,
    };
  }, [data]);

  // Filtered Users List
  const filteredUsers = useMemo(() => {
    return Object.values(data.userSummaries || {})
      .filter((user) => {
        if (!isDateInRange(user.lastActiveAt)) return false;

        if (searchQuery.trim()) {
          const matches = matchesSearch(searchQuery, {
            primary: [user.userName, user.userEmail],
            secondary: user.primaryTeam
          });
          if (!matches) return false;
        }

        if (selectedSource !== 'All') {
          const hasSource = Object.values(user.files || {}).some(
            (f) => f.source === selectedSource
          );
          if (!hasSource) return false;
        }

        return true;
      })
      .sort((a, b) => (b.totalOpens || 0) - (a.totalOpens || 0));
  }, [data.userSummaries, searchQuery, selectedSource, isDateInRange]);

  // Filtered Files List
  const filteredFiles = useMemo(() => {
    return Object.values(data.fileSummaries || {})
      .filter((file) => {
        if (!isDateInRange(file.lastOpenedAt)) return false;

        if (selectedSource !== 'All' && file.source !== selectedSource) {
          return false;
        }

        if (searchQuery.trim()) {
          const matches = matchesSearch(searchQuery, {
            primary: file.fileName,
            secondary: [file.category, file.fileUrl],
            tertiary: file.team
          });
          if (!matches) return false;
        }

        return true;
      })
      .sort((a, b) => (b.totalOpens || 0) - (a.totalOpens || 0));
  }, [data.fileSummaries, searchQuery, selectedSource, isDateInRange]);

  // Filtered Recent Events List
  const filteredEvents = useMemo(() => {
    return (data.recentEvents || []).filter((event) => {
      if (!isDateInRange(event.timestamp)) return false;

      if (selectedSource !== 'All' && event.source !== selectedSource) {
        return false;
      }

      if (searchQuery.trim()) {
        const matches = matchesSearch(searchQuery, {
          primary: [event.fileName, event.userEmail, event.userName],
          secondary: [event.category, event.fileUrl],
          tertiary: [event.team, event.action]
        });
        if (!matches) return false;
      }

      return true;
    });
  }, [data.recentEvents, searchQuery, selectedSource, isDateInRange]);

  const formatTimestamp = (iso: string) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const getSourceBadgeColor = (source: UsageSourceColumn) => {
    switch (source) {
      case 'Daily Tasks':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/70 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800';
      case 'Printable Documents':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-950/70 dark:text-blue-300 border-blue-300 dark:border-blue-800';
      case 'All Sheets':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-950/70 dark:text-purple-300 border-purple-300 dark:border-purple-800';
      case 'Bookmarks':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-950/70 dark:text-amber-300 border-amber-300 dark:border-amber-800';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300 border-gray-300 dark:border-gray-700';
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    void navigator.clipboard.writeText(text);
    setCopyFeedback(label);
    setTimeout(() => setCopyFeedback(null), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Top Banner & KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* KPI 1: Total Opens */}
        <div className="p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Total Opens
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {metrics.totalOpens.toLocaleString()}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              Across all sheets & docs
            </p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-950/50 flex items-center justify-center text-blue-600 dark:text-blue-400 text-xl font-bold">
            📈
          </div>
        </div>

        {/* KPI 2: Active Users */}
        <div className="p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Active Users
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {metrics.uniqueUsers}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              {metrics.topUser ? `Top: ${metrics.topUser.name}` : 'Unique employees'}
            </p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 flex items-center justify-center text-emerald-600 dark:text-emerald-400 text-xl font-bold">
            👥
          </div>
        </div>

        {/* KPI 3: Unique Files Accessed */}
        <div className="p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Files Accessed
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {metrics.uniqueFiles}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate max-w-[180px]" title={metrics.topFile?.name}>
              {metrics.topFile ? `Top: ${metrics.topFile.name}` : 'Different resources'}
            </p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-950/50 flex items-center justify-center text-purple-600 dark:text-purple-400 text-xl font-bold">
            📁
          </div>
        </div>

        {/* KPI 4: Last Activity */}
        <div className="p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Last Activity
            </p>
            <p className="text-sm font-bold text-gray-900 dark:text-white mt-2 truncate max-w-[160px]">
              {data.recentEvents && data.recentEvents[0]
                ? formatTimestamp(data.recentEvents[0].timestamp)
                : 'No recent events'}
            </p>
            <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mt-0.5">
              Live sync active
            </p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-amber-50 dark:bg-amber-950/50 flex items-center justify-center text-amber-600 dark:text-amber-400 text-xl font-bold">
            ⏱️
          </div>
        </div>
      </div>

      {/* Control Bar: View Switcher, Search, Filters, and Export */}
      <div className="p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        {/* Left: View Tabs */}
        <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-900 p-1 rounded-xl self-start">
          <button
            onClick={() => setViewMode('by_user')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
              viewMode === 'by_user'
                ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <span>👤</span>
            <span>By User ({filteredUsers.length})</span>
          </button>

          <button
            onClick={() => setViewMode('by_file')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
              viewMode === 'by_file'
                ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <span>📄</span>
            <span>By File / Link ({filteredFiles.length})</span>
          </button>

          <button
            onClick={() => setViewMode('activity_log')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
              viewMode === 'activity_log'
                ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <span>📜</span>
            <span>Recent Activity ({filteredEvents.length})</span>
          </button>
        </div>

        {/* Right: Search, Filters & Actions */}
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Search Input */}
          <div className="relative min-w-[200px] flex-1 sm:flex-initial">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
              🔍
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search user, file, team..."
              className="w-full pl-9 pr-3 py-1.5 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xs"
              >
                ✕
              </button>
            )}
          </div>

          {/* Source Filter */}
          <select
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value as any)}
            className="py-1.5 px-2.5 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
          >
            <option value="All">All Sources</option>
            <option value="Daily Tasks">Daily Tasks</option>
            <option value="Printable Documents">Printable Documents</option>
            <option value="All Sheets">All Sheets</option>
            <option value="Bookmarks">Bookmarks</option>
          </select>

          {/* Date Range Filter */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as any)}
            className="py-1.5 px-2.5 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="7days">Past 7 Days</option>
            <option value="30days">Past 30 Days</option>
          </select>

          {/* Refresh Button */}
          <button
            onClick={() => void refreshData()}
            disabled={loading}
            title="Refresh analytics from server"
            className="p-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors flex items-center justify-center"
          >
            <span className={`text-sm ${loading ? 'animate-spin' : ''}`}>🔄</span>
          </button>

          {/* Export CSV Dropdown */}
          <div className="relative">
            <button
              onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 shadow-sm"
            >
              <span>📥</span>
              <span>Export CSV</span>
              <span className="text-[10px]">▼</span>
            </button>

            {exportDropdownOpen && (
              <div
                className="absolute right-0 mt-1 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl py-1 z-30"
                onClick={() => setExportDropdownOpen(false)}
              >
                <button
                  onClick={() => exportUsageToCsv(data, 'users')}
                  className="w-full text-left px-4 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                >
                  <span>👤</span>
                  <div>
                    <p className="font-semibold">User Usage Breakdown</p>
                    <p className="text-[10px] text-gray-400">User × File × Open Count</p>
                  </div>
                </button>

                <button
                  onClick={() => exportUsageToCsv(data, 'files')}
                  className="w-full text-left px-4 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                >
                  <span>📄</span>
                  <div>
                    <p className="font-semibold">File Usage Summary</p>
                    <p className="text-[10px] text-gray-400">File × Total Opens × Unique Users</p>
                  </div>
                </button>

                <button
                  onClick={() => exportUsageToCsv(data, 'events')}
                  className="w-full text-left px-4 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                >
                  <span>📜</span>
                  <div>
                    <p className="font-semibold">Raw Activity History</p>
                    <p className="text-[10px] text-gray-400">Timestamped click event feed</p>
                  </div>
                </button>
              </div>
            )}
          </div>

          {/* SuperAdmin Clear Logs Button */}
          {isSuperAdmin && (
            <button
              onClick={() => setShowClearConfirmModal(true)}
              title="Clear all recorded usage data"
              className="p-1.5 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 dark:hover:bg-rose-900/50 text-rose-600 dark:text-rose-400 rounded-lg transition-colors flex items-center justify-center"
            >
              <span className="text-sm">🗑️</span>
            </button>
          )}
        </div>
      </div>

      {copyFeedback && (
        <div className="fixed bottom-6 right-6 z-50 bg-gray-900 text-white text-xs px-3.5 py-2 rounded-lg shadow-xl animate-fade-in flex items-center gap-2 border border-gray-700">
          <span>✓</span> Copied {copyFeedback} to clipboard!
        </div>
      )}

      {/* ===================================================================== */}
      {/* VIEW 1: BY USER VIEW                                                 */}
      {/* ===================================================================== */}
      {viewMode === 'by_user' && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">
              Employees & File Access Counts ({filteredUsers.length})
            </h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Click any employee to see every link & file they opened
            </span>
          </div>

          {filteredUsers.length === 0 ? (
            <div className="py-12 text-center text-gray-500 dark:text-gray-400 text-sm">
              No employee access events recorded for this selection.
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredUsers.map((user) => {
                const isExpanded = expandedUser === user.userEmail;
                const userFiles = Object.values(user.files || {}).sort(
                  (a, b) => (b.count || 0) - (a.count || 0)
                );

                return (
                  <div key={user.userEmail} className="transition-colors hover:bg-gray-50/60 dark:hover:bg-gray-750/50">
                    <div
                      onClick={() => setExpandedUser(isExpanded ? null : user.userEmail)}
                      className="px-6 py-3.5 flex items-center justify-between cursor-pointer gap-4"
                    >
                      {/* Left: User Info */}
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 font-bold flex items-center justify-center text-xs flex-shrink-0">
                          {user.userName ? user.userName.charAt(0).toUpperCase() : user.userEmail.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                            {user.userName || user.userEmail}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {user.userEmail}
                            {user.primaryTeam && (
                              <span className="ml-2 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px] font-medium text-gray-700 dark:text-gray-300">
                                {user.primaryTeam}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Right: Metrics & Expand Indicator */}
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <div className="text-right">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                            {user.totalOpens} {user.totalOpens === 1 ? 'open' : 'opens'}
                          </span>
                          <p className="text-[10px] text-gray-400 mt-0.5">
                            Last active: {formatTimestamp(user.lastActiveAt)}
                          </p>
                        </div>

                        <span className={`text-gray-400 transition-transform duration-200 text-xs ${isExpanded ? 'rotate-180' : ''}`}>
                          ▼
                        </span>
                      </div>
                    </div>

                    {/* Expanded Drawer: All files opened by this user */}
                    {isExpanded && (
                      <div className="px-6 pb-4 pt-1 bg-gray-50 dark:bg-gray-900/60 border-t border-gray-100 dark:border-gray-800">
                        <div className="mb-2 flex items-center justify-between">
                          <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                            Files & Documents Opened by {user.userName || user.userEmail} ({userFiles.length})
                          </p>
                        </div>

                        <div className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden bg-white dark:bg-gray-800 shadow-inner">
                          <table className="w-full text-left text-xs">
                            <thead className="bg-gray-100 dark:bg-gray-850 text-gray-600 dark:text-gray-400 font-semibold border-b border-gray-200 dark:border-gray-800">
                              <tr>
                                <th className="py-2 px-3">File / Resource Name</th>
                                <th className="py-2 px-3">Source Category</th>
                                <th className="py-2 px-3 text-center">Times Opened</th>
                                <th className="py-2 px-3">Last Opened</th>
                                <th className="py-2 px-3 text-right">Action</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                              {userFiles.map((f) => (
                                <tr key={f.fileId} className="hover:bg-gray-50/80 dark:hover:bg-gray-750/50">
                                  <td className="py-2 px-3 font-medium text-gray-900 dark:text-white max-w-[280px] truncate" title={f.fileName}>
                                    {f.fileName}
                                  </td>
                                  <td className="py-2 px-3">
                                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold border ${getSourceBadgeColor(f.source)}`}>
                                      {f.source}
                                    </span>
                                  </td>
                                  <td className="py-2 px-3 text-center">
                                    <span className="font-bold text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-xs">
                                      {f.count}
                                    </span>
                                  </td>
                                  <td className="py-2 px-3 text-gray-500 dark:text-gray-400">
                                    {formatTimestamp(f.lastOpenedAt)}
                                  </td>
                                  <td className="py-2 px-3 text-right">
                                    {f.fileUrl ? (
                                      <a
                                        href={f.fileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-500 dark:text-blue-400 font-bold inline-flex items-center gap-1"
                                      >
                                        <span>Open</span> ↗
                                      </a>
                                    ) : (
                                      <span className="text-gray-400">—</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ===================================================================== */}
      {/* VIEW 2: BY FILE / LINK VIEW                                          */}
      {/* ===================================================================== */}
      {viewMode === 'by_file' && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">
              Files, Sheets & Links Usage ({filteredFiles.length})
            </h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Click any file to see which employees opened it and how many times
            </span>
          </div>

          {filteredFiles.length === 0 ? (
            <div className="py-12 text-center text-gray-500 dark:text-gray-400 text-sm">
              No files match the selected filter.
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredFiles.map((file) => {
                const isExpanded = expandedFile === file.fileId;
                const fileUsers = Object.values(file.userCounts || {}).sort(
                  (a, b) => (b.count || 0) - (a.count || 0)
                );

                return (
                  <div key={file.fileId} className="transition-colors hover:bg-gray-50/60 dark:hover:bg-gray-750/50">
                    <div
                      onClick={() => setExpandedFile(isExpanded ? null : file.fileId)}
                      className="px-6 py-3.5 flex items-center justify-between cursor-pointer gap-4"
                    >
                      {/* Left: File Info */}
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-xl bg-purple-50 dark:bg-purple-950/50 text-purple-600 dark:text-purple-400 font-bold flex items-center justify-center text-base flex-shrink-0">
                          {file.source === 'Printable Documents' ? '📄' : file.source === 'Bookmarks' ? '🔖' : '📊'}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-gray-900 dark:text-white truncate" title={file.fileName}>
                            {file.fileName}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold border ${getSourceBadgeColor(file.source)}`}>
                              {file.source}
                            </span>
                            {file.category && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {file.category}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right: Metrics & Expand Indicator */}
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <div className="text-right">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-purple-50 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
                            {file.totalOpens} {file.totalOpens === 1 ? 'total open' : 'total opens'}
                          </span>
                          <p className="text-[10px] text-gray-400 mt-0.5">
                            {fileUsers.length} {fileUsers.length === 1 ? 'user' : 'users'} • Last: {formatTimestamp(file.lastOpenedAt)}
                          </p>
                        </div>

                        <span className={`text-gray-400 transition-transform duration-200 text-xs ${isExpanded ? 'rotate-180' : ''}`}>
                          ▼
                        </span>
                      </div>
                    </div>

                    {/* Expanded Drawer: Users who opened this file */}
                    {isExpanded && (
                      <div className="px-6 pb-4 pt-1 bg-gray-50 dark:bg-gray-900/60 border-t border-gray-100 dark:border-gray-800">
                        <div className="mb-2 flex items-center justify-between">
                          <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                            Users Who Opened This File ({fileUsers.length})
                          </p>
                          {file.fileUrl && (
                            <a
                              href={file.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-500 dark:text-blue-400 font-bold inline-flex items-center gap-1"
                            >
                              <span>Direct Link</span> ↗
                            </a>
                          )}
                        </div>

                        <div className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden bg-white dark:bg-gray-800 shadow-inner">
                          <table className="w-full text-left text-xs">
                            <thead className="bg-gray-100 dark:bg-gray-850 text-gray-600 dark:text-gray-400 font-semibold border-b border-gray-200 dark:border-gray-800">
                              <tr>
                                <th className="py-2 px-3">Employee Name</th>
                                <th className="py-2 px-3">Employee Email</th>
                                <th className="py-2 px-3 text-center">Times Opened</th>
                                <th className="py-2 px-3 text-right">Last Opened</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                              {fileUsers.map((u) => (
                                <tr key={u.userEmail} className="hover:bg-gray-50/80 dark:hover:bg-gray-750/50">
                                  <td className="py-2 px-3 font-semibold text-gray-900 dark:text-white">
                                    {u.userName || u.userEmail}
                                  </td>
                                  <td className="py-2 px-3 text-gray-500 dark:text-gray-400">
                                    {u.userEmail}
                                  </td>
                                  <td className="py-2 px-3 text-center">
                                    <span className="font-bold text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-xs">
                                      {u.count}
                                    </span>
                                  </td>
                                  <td className="py-2 px-3 text-right text-gray-500 dark:text-gray-400">
                                    {formatTimestamp(u.lastOpenedAt)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ===================================================================== */}
      {/* VIEW 3: RECENT ACTIVITY EVENT LOG                                    */}
      {/* ===================================================================== */}
      {viewMode === 'activity_log' && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">
              Chronological Access Stream ({filteredEvents.length} events)
            </h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Real-time audit log of user clicks
            </span>
          </div>

          {filteredEvents.length === 0 ? (
            <div className="py-12 text-center text-gray-500 dark:text-gray-400 text-sm">
              No recent click events found matching the criteria.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-100 dark:bg-gray-850 text-gray-600 dark:text-gray-400 font-semibold border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="py-3 px-4">Timestamp</th>
                    <th className="py-3 px-4">Employee</th>
                    <th className="py-3 px-4">File / Link Name</th>
                    <th className="py-3 px-4">Source Category</th>
                    <th className="py-3 px-4">Action</th>
                    <th className="py-3 px-4 text-right">Link</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {filteredEvents.map((e: UsageLogEvent) => (
                    <tr key={e.id} className="hover:bg-gray-50/80 dark:hover:bg-gray-750/50">
                      <td className="py-2.5 px-4 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatTimestamp(e.timestamp)}
                      </td>
                      <td className="py-2.5 px-4 whitespace-nowrap">
                        <div className="font-semibold text-gray-900 dark:text-white">
                          {e.userName || e.userEmail}
                        </div>
                        <div className="text-[10px] text-gray-400">
                          {e.userEmail}
                        </div>
                      </td>
                      <td className="py-2.5 px-4 max-w-[320px] truncate font-medium text-gray-900 dark:text-white" title={e.fileName}>
                        {e.fileName}
                      </td>
                      <td className="py-2.5 px-4 whitespace-nowrap">
                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold border ${getSourceBadgeColor(e.source)}`}>
                          {e.source}
                        </span>
                      </td>
                      <td className="py-2.5 px-4 text-gray-600 dark:text-gray-300 capitalize whitespace-nowrap">
                        {e.action ? e.action.replace(/_/g, ' ') : 'open link'}
                      </td>
                      <td className="py-2.5 px-4 text-right whitespace-nowrap">
                        {e.fileUrl ? (
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => copyToClipboard(e.fileUrl, 'URL')}
                              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                              title="Copy URL"
                            >
                              📋
                            </button>
                            <a
                              href={e.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-500 dark:text-blue-400 font-bold"
                              title="Open link"
                            >
                              ↗
                            </a>
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Clear Logs Confirmation Modal */}
      {showClearConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-200 dark:border-gray-700 animate-scale-up">
            <div className="w-12 h-12 rounded-full bg-rose-100 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 flex items-center justify-center text-2xl mx-auto mb-4">
              ⚠️
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white text-center">
              Clear All Usage Logs?
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">
              This will permanently wipe all recorded file open counts, user telemetry, and activity history from Cloudflare KV. This action cannot be undone.
            </p>

            <div className="mt-4">
              <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                Type <span className="text-rose-600 font-black">CLEAR</span> to confirm:
              </label>
              <input
                type="text"
                value={clearInputConfirm}
                onChange={(e) => setClearInputConfirm(e.target.value)}
                placeholder="CLEAR"
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-rose-500"
              />
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowClearConfirmModal(false);
                  setClearInputConfirm('');
                }}
                disabled={clearLoading}
                className="px-4 py-2 text-xs font-bold text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => void handleClearLogs()}
                disabled={clearInputConfirm.trim().toUpperCase() !== 'CLEAR' || clearLoading}
                className="px-4 py-2 text-xs font-bold text-white bg-rose-600 hover:bg-rose-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-1.5 shadow-sm"
              >
                {clearLoading ? 'Clearing...' : 'Confirm & Wipe Logs'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
